import { useState } from "react";
import { Loader2, Lock, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { api, apiError } from "@/lib/api";

// Clients are gated by the change-password dialog in ClientPortal.jsx.
const GATED_ROLES = ["enterprise", "superadmin"];

export function ForcePasswordChange() {
  const { user, refreshUser, logout } = useAuth();
  const [pw, setPw] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);

  const eligible = user && user.must_change_password && GATED_ROLES.includes(user.role);
  if (!eligible) return null;

  const endpoint = user.role === "client" ? "/client/change-password" : "/enterprise/change-password";

  const submit = async (e) => {
    e.preventDefault();
    if (pw.length < 8) { toast.error("Password must be at least 8 characters."); return; }
    if (pw !== confirm) { toast.error("Passwords don't match."); return; }
    setBusy(true);
    try {
      await api.post(endpoint, { new_password: pw });
      await refreshUser();
      toast.success("Password updated — you're all set.");
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div data-testid="force-password-modal" className="fixed inset-0 z-[100] bg-ally-ink/95 backdrop-blur-sm flex items-center justify-center p-6">
      <div className="w-full max-w-md bg-white text-ally-ink rounded-[28px] p-8 sm:p-10">
        <div className="w-14 h-14 rounded-2xl bg-ally-ink flex items-center justify-center text-ally-pink"><Lock size={24} /></div>
        <h1 className="font-display text-2xl font-black mt-6">Set a new password</h1>
        <p className="text-ally-slate mt-2 text-sm">For your security, please choose a new password before continuing. This replaces the temporary password you were given.</p>
        <form onSubmit={submit} className="space-y-4 mt-6">
          <input data-testid="force-password-new" type="password" placeholder="New password (min 8 characters)" value={pw}
            onChange={(e) => setPw(e.target.value)} autoFocus required
            className="w-full bg-[#FAFAFA] border-2 border-black/5 rounded-2xl px-5 py-3.5 outline-none focus:border-ally-pink transition-colors" />
          <input data-testid="force-password-confirm" type="password" placeholder="Confirm new password" value={confirm}
            onChange={(e) => setConfirm(e.target.value)} required
            className="w-full bg-[#FAFAFA] border-2 border-black/5 rounded-2xl px-5 py-3.5 outline-none focus:border-ally-pink transition-colors" />
          <button data-testid="force-password-submit" disabled={busy}
            className="w-full bg-ally-ink text-white rounded-full px-8 py-4 font-bold flex items-center justify-center gap-2 disabled:opacity-50">
            {busy ? <Loader2 className="animate-spin" size={18} /> : <><ShieldCheck size={18} /> Save &amp; continue</>}
          </button>
        </form>
        <button onClick={logout} data-testid="force-password-logout" className="w-full text-ally-slate text-sm font-bold hover:text-ally-ink mt-4">Sign out instead</button>
      </div>
    </div>
  );
}
